import { useEffect, useState } from "react";
import { CheckCircle, FloppyDisk, CircleNotch, UploadSimple, X, Download, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELDS = [
  { key: "rate_24k", label: "24K Gold", unit: "₹/g" },
  { key: "rate_22k", label: "22K Gold", unit: "₹/g" },
  { key: "rate_18k", label: "18K Gold", unit: "₹/g" },
  { key: "silver", label: "Silver", unit: "₹/g" },
];

const SAMPLE_CSV = `city,rate_24k,rate_22k,rate_18k,silver
chennai,7250,6650,5450,98
mumbai,7270,6670,5470,99
delhi,7280,6680,5480,98
bangalore,7260,6660,5460,98
hyderabad,7255,6655,5455,98
kolkata,7275,6675,5475,98`;

export default function AdminBoardRates() {
  const [rates, setRates] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingCity, setSavingCity] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState(SAMPLE_CSV);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [goldApi, setGoldApi] = useState({ configured: false, last_fetch: null });
  const [syncing, setSyncing] = useState(false);

  const loadGoldApi = async () => {
    try {
      const r = await api.get("/admin/gold-rates/status");
      setGoldApi(r.data);
    } catch {
      // ignore
    }
  };

  const syncFromGoldApi = async () => {
    if (!window.confirm("Pull live rates from GoldAPI.io and update all 6 city board rates? Each city's premium-over-Chennai will be preserved.")) return;
    setSyncing(true);
    try {
      const r = await api.post("/admin/gold-rates/sync?force=true");
      toast.success(`Synced — 22K base ₹${r.data.live.rate_22k}/g applied to ${r.data.updated.length} cities`);
      await load();
      await loadGoldApi();
    } catch (err) {
      toast.error(err.response?.data?.detail || "GoldAPI sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/board-rates");
      setRates(r.data.rates || []);
      const d = {};
      (r.data.rates || []).forEach((row) => {
        d[row.city] = {
          rate_24k: row.rate_24k,
          rate_22k: row.rate_22k,
          rate_18k: row.rate_18k,
          silver: row.silver,
        };
      });
      setDraft(d);
    } catch (err) {
      toast.error("Could not load rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadGoldApi();
  }, []);

  const update = (city, key, value) => {
    setDraft((d) => ({
      ...d,
      [city]: { ...d[city], [key]: value === "" ? "" : Number(value) },
    }));
  };

  const isDirty = (city) => {
    const orig = rates.find((r) => r.city === city);
    if (!orig || !draft[city]) return false;
    return FIELDS.some((f) => Number(draft[city][f.key]) !== Number(orig[f.key]));
  };

  const save = async (city) => {
    const d = draft[city];
    if (FIELDS.some((f) => !d[f.key] || isNaN(Number(d[f.key])))) {
      toast.error("All rates must be numbers > 0");
      return;
    }
    setSavingCity(city);
    try {
      const r = await api.put(`/admin/board-rates/${city}`, {
        rate_24k: Number(d.rate_24k),
        rate_22k: Number(d.rate_22k),
        rate_18k: Number(d.rate_18k),
        silver: Number(d.silver),
      });
      setRates((rs) => rs.map((row) => (row.city === city ? r.data.rate : row)));
      toast.success(`${city.charAt(0).toUpperCase() + city.slice(1)} rates updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingCity(null);
    }
  };

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) throw new Error("Need header + at least one row");
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const required = ["city", "rate_24k", "rate_22k", "rate_18k", "silver"];
    for (const r of required) {
      if (!headers.includes(r)) throw new Error(`Missing column: ${r}`);
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(",").map((c) => c.trim());
      if (cells.length < headers.length) continue;
      const row = {};
      headers.forEach((h, idx) => (row[h] = cells[idx]));
      rows.push({
        city: row.city.toLowerCase(),
        rate_24k: Number(row.rate_24k),
        rate_22k: Number(row.rate_22k),
        rate_18k: Number(row.rate_18k),
        silver: Number(row.silver),
      });
      const r = rows[rows.length - 1];
      if ([r.rate_24k, r.rate_22k, r.rate_18k, r.silver].some((v) => isNaN(v) || v <= 0)) {
        throw new Error(`Invalid number on line ${i + 1}`);
      }
    }
    return rows;
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mineai-board-rates-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setBulkText(String(e.target?.result || ""));
    reader.readAsText(file);
  };

  const submitBulk = async () => {
    let rows;
    try {
      rows = parseCsv(bulkText);
    } catch (err) {
      toast.error(err.message || "Invalid CSV");
      return;
    }
    if (!rows.length) {
      toast.error("No rows to import");
      return;
    }
    setBulkSaving(true);
    try {
      const r = await api.post("/admin/board-rates/bulk", { rows });
      toast.success(`Updated ${r.data.count} cities`);
      if (r.data.errors?.length) {
        toast.error(`Skipped: ${r.data.errors.map((e) => e.city).join(", ")}`);
      }
      setBulkOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <div data-testid="admin-board-rates-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            Board Rates
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            City-wise gold & silver rates
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            These rates flow live to the public Price Check page. Update once a day or whenever the local jewellery board posts new rates.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="goldapi-sync-btn"
            onClick={syncFromGoldApi}
            disabled={!goldApi.configured || syncing}
            title={goldApi.configured ? "Pull live rates from GoldAPI.io" : "Set GOLDAPI_KEY in backend/.env to enable"}
            className="px-4 py-2.5 rounded-md border border-[#D4AF37] text-[#8a6d1c] hover:bg-[#D4AF37] hover:text-white smooth flex items-center gap-2 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <CircleNotch size={16} className="animate-spin" weight="bold" />
            ) : (
              <ArrowsClockwise size={16} weight="duotone" />
            )}
            {syncing ? "Syncing..." : "Sync from GoldAPI"}
          </button>
          <button
            data-testid="bulk-import-btn"
            onClick={() => setBulkOpen(true)}
            className="px-4 py-2.5 rounded-md border border-[#3B5BDB] text-[#3B5BDB] hover:bg-[#3B5BDB] hover:text-white smooth flex items-center gap-2 text-sm font-medium"
          >
            <UploadSimple size={16} weight="duotone" /> Bulk import
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-3 text-[#4A5568]">
          <CircleNotch size={18} className="animate-spin" weight="bold" />
          Loading rates...
        </div>
      ) : (
        <div className="mt-8 space-y-5">
          {rates.map((row) => (
            <div
              key={row.city}
              className="mc-card p-5 sm:p-6"
              data-testid={`row-${row.city}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-display text-2xl text-[#0A1128]">
                    {row.city_label}
                  </h2>
                  <p className="text-[11px] text-[#4A5568]">
                    Last updated: {row.updated_at ? new Date(row.updated_at).toLocaleString() : "never"}
                    {row.updated_by ? ` · by ${row.updated_by}` : ""}
                  </p>
                </div>
                <button
                  data-testid={`save-${row.city}`}
                  onClick={() => save(row.city)}
                  disabled={!isDirty(row.city) || savingCity === row.city}
                  className="px-4 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingCity === row.city ? (
                    <CircleNotch size={14} className="animate-spin" weight="bold" />
                  ) : isDirty(row.city) ? (
                    <FloppyDisk size={14} weight="duotone" />
                  ) : (
                    <CheckCircle size={14} weight="duotone" />
                  )}
                  {savingCity === row.city ? "Saving..." : isDirty(row.city) ? "Save changes" : "Saved"}
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
                      {f.label} ({f.unit})
                    </label>
                    <input
                      type="number"
                      data-testid={`input-${row.city}-${f.key}`}
                      value={draft[row.city]?.[f.key] ?? ""}
                      onChange={(e) => update(row.city, f.key, e.target.value)}
                      className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] tabular-nums"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {bulkOpen && (
        <div
          className="fixed inset-0 z-50 bg-[#0A1128]/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && setBulkOpen(false)}
          data-testid="bulk-modal"
        >
          <div className="bg-white rounded-2xl border border-[#E8DFD1] w-full max-w-2xl my-auto">
            <div className="px-6 py-4 border-b border-[#E8DFD1] flex items-center justify-between">
              <h2 className="font-display text-xl text-[#0A1128]">Bulk import rates</h2>
              <button onClick={() => setBulkOpen(false)} className="text-[#4A5568] hover:text-[#0A1128]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-[#4A5568]">
                Paste CSV with header <code className="bg-[#F5EFE6] px-1.5 py-0.5 rounded text-xs">city,rate_24k,rate_22k,rate_18k,silver</code>, or upload a CSV file.
                Cities allowed: chennai, mumbai, delhi, bangalore, hyderabad, kolkata.
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="bulk-template-btn"
                  onClick={downloadTemplate}
                  className="px-3 py-2 rounded-md border border-[#E8DFD1] text-xs hover:border-[#3B5BDB] smooth flex items-center gap-1.5"
                >
                  <Download size={14} /> Download template
                </button>
                <label className="px-3 py-2 rounded-md border border-[#E8DFD1] text-xs hover:border-[#3B5BDB] smooth flex items-center gap-1.5 cursor-pointer">
                  <UploadSimple size={14} /> Upload .csv
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    data-testid="bulk-file"
                    onChange={(e) => onPickFile(e.target.files?.[0])}
                  />
                </label>
              </div>
              <textarea
                data-testid="bulk-textarea"
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={10}
                spellCheck="false"
                className="w-full px-3 py-2 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-mono text-sm tabular-nums"
              />
            </div>
            <div className="px-6 py-4 border-t border-[#E8DFD1] flex justify-end gap-2">
              <button
                onClick={() => setBulkOpen(false)}
                className="px-4 py-2 rounded-md border border-[#E8DFD1] text-sm hover:border-[#0A1128] smooth"
              >
                Cancel
              </button>
              <button
                data-testid="bulk-submit"
                onClick={submitBulk}
                disabled={bulkSaving}
                className="px-4 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
              >
                {bulkSaving ? "Importing..." : "Import & save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
